import { detachIVS } from './ivs';
import { composeKey } from './key';
const FORMATS = {
    h264: 'video/mp4; codecs="avc1.42E01E"',
    hevc: 'video/mp4; codecs="hvc1"',
};
const VIDEO_FILES_PRIORITY = [
    'horizontal_hevc',
    'horizontal_h264',
    'vertical_hevc',
    'vertical_h264',
    'frameless_hevc',
    'frameless_h264',
];
/**
 * Checks compatibility of the browser for the different media formats.
 * @param {object} videoFile Video file object
 * @return {boolean} True if its compatible
 */
const isFormatCompatible = (videoFile) => {
    const video = document.createElement('video');
    if (!videoFile.format) {
        throw new Error(`unrecognized video file format ${videoFile.format}`);
    }
    const canPlay = video.canPlayType(FORMATS[videoFile.format]);
    switch (canPlay) {
        case 'probably':
        case 'maybe':
            return true;
        default:
            return false;
    }
};
/**
 * Orders a video.video_files based on specific priority.
 * @param {object} video Video object
 * @return {object} Video with ordered video_files
 */
export const orderVideoFiles = (video) => {
    const videoFiles = video.video_files || [];
    // Map each video file to object for constant lookup
    const filtered = {};
    videoFiles.forEach((file) => {
        if (file.has_watermark) {
            filtered[`${file.video_file_type}_${file.format}`] = file;
        }
    });
    // Rearrange based on priority
    const orderedFiles = [];
    VIDEO_FILES_PRIORITY.forEach((key) => {
        const vf = filtered[key];
        if (vf && isFormatCompatible(vf)) {
            orderedFiles.push(vf);
        }
    });
    if (video.download_url) {
        orderedFiles.push({ file_url: video.download_url, format: null });
    }
    return orderedFiles;
};
/**
 * Attaches <source> nodes to existing video element
 * @param {object} videoElement HTML Video Element
 * @param {object} video Video object
 * @return void
 */
export const attachSortedVideoFiles = (videoElement, video) => {
    // Create a unique key so we ensure
    // single execution per video
    const key = composeKey(video);
    if (videoElement.getAttribute('data-sourcekey') === key) {
        return;
    }
    detachSortedVideoFiles(videoElement);
    detachIVS(videoElement);
    videoElement.setAttribute('data-sourcekey', key);
    videoElement.setAttribute('data-encoded_id', video.encoded_id);
    videoElement.removeAttribute('src');
    while (videoElement.firstChild) {
        videoElement.firstChild.remove();
    }
    orderVideoFiles(video)
        .map((file) => {
        const el = document.createElement('source');
        el.src = file.file_url;
        el.type = file.format ? `video/mp4; codecs=${file.format}` : `video/mp4`;
        return el;
    })
        .forEach((source) => videoElement.appendChild(source));
    // Always trigger load when sources gets changed
    videoElement.currentTime = 0;
    videoElement.load();
};
/**
 * Detaches <source> nodes from provided video element
 * @param {object} videoElement HTML Video Element
 * @return void
 */
export const detachSortedVideoFiles = (videoElement) => {
    videoElement.style.visibility = 'hidden';
    videoElement.src = '';
    videoElement.querySelectorAll('source').forEach((source) => {
        source.remove();
    });
    videoElement.load();
};
